import { type EtapaInput, produtoSchema } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import {
  BarraTitulo,
  BotaoEditar,
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Button } from '@/components/ui/button'
import { Campo, Checkbox, Input, Select } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Ativo, Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/produtos')({ component: Produtos })

type ProdutoItem = NonNullable<ReturnType<typeof useProdutos>['data']>[number]
function useProdutos() {
  const trpc = useTRPC()
  return useQuery(trpc.cadastros.produtos.listar.queryOptions())
}

const etapaVazia = (setorId = ''): EtapaInput => ({
  setorId,
  nome: '',
  pecasPorHora: 0,
  tempoPresetSeg: 0,
})

function Produtos() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useProdutos()
  const setores = useQuery(trpc.cadastros.setores.listar.queryOptions())
  const linhas = useQuery(trpc.cadastros.linhas.listar.queryOptions())
  const g = useGavetaCadastro<ProdutoItem>()
  // Sequência é controlada (ordenar/adicionar/remover); carregada ao abrir a gaveta.
  const [etapas, setEtapas] = useState<EtapaInput[]>([etapaVazia()])
  const abrirNovo = () => {
    setEtapas([etapaVazia()])
    g.abrirNovo()
  }
  const abrirEditar = (p: ProdutoItem) => {
    setEtapas(
      p.etapas.map((e) => ({
        setorId: e.setorId,
        nome: e.nome,
        pecasPorHora: e.pecasPorHora,
        tempoPresetSeg: e.tempoPresetSeg,
      })),
    )
    g.abrirEditar(p)
  }

  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.cadastros.produtos.listar.queryKey() })
  const criar = useMutation(
    trpc.cadastros.produtos.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.produtos.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const submit = aoSubmeter({
    schema: produtoSchema,
    numericos: ['tamanhoLote', 'multiploCaixa'],
    opcionais: ['linhaId'],
    booleanos: ['ativo'],
    extra: { etapas },
    setErros: g.setErros,
    sucesso: (d) => `Produto ${d.nome} salvo.`,
    onValido: (dados) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...dados }) : criar.mutateAsync(dados),
  })

  const temHistorico = (g.item?._count.itens ?? 0) > 0
  const mudarEtapa = (i: number, patch: Partial<EtapaInput>) =>
    setEtapas((es) => es.map((e, j) => (j === i ? { ...e, ...patch } : e)))
  const mover = (i: number, delta: -1 | 1) =>
    setEtapas((es) => {
      const j = i + delta
      if (j < 0 || j >= es.length) return es
      const copia = [...es]
      ;[copia[i], copia[j]] = [copia[j]!, copia[i]!]
      return copia
    })

  return (
    <>
      <BarraTitulo
        titulo="Produtos"
        descricao="Cada produto tem sua sequência: por quais setores passa, em que ordem e com que tempo previsto (os 8–10% de tolerância já vão embutidos aqui)."
        onNovo={abrirNovo}
        rotuloNovo="Novo produto"
      />
      <Tabela>
        <Cabecalho>
          <Th>Código</Th>
          <Th>Nome</Th>
          <Th>Linha</Th>
          <Th>Ficha</Th>
          <Th>Sequência</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((p) => (
            <Linha key={p.id}>
              <Td className="font-mono text-xs">{p.codigo}</Td>
              <Td className="font-medium">
                {p.nome} {!p.ativo && <Ativo ativo={false} />}
              </Td>
              <Td className="text-fg-muted">{p.linha?.nome ?? '—'}</Td>
              <Td numerico>
                {p.tamanhoLote}
                {p.multiploCaixa ? ` (caixa ${p.multiploCaixa})` : ''}
              </Td>
              <Td className="text-xs text-fg-muted">
                {p.etapas
                  .map(
                    (e) =>
                      `${e.setor.nome} ${e.pecasPorHora > 0 ? `${Math.round(e.pecasPorHora)}/h` : 'sem tempo'}`,
                  )
                  .join(' → ')}
              </Td>
              <Td className="text-right">
                <BotaoEditar rotulo={p.nome} onClick={() => abrirEditar(p)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={6} />}
          {lista.data?.length === 0 && <Vazio colunas={6}>Nenhum produto cadastrado.</Vazio>}
        </tbody>
      </Tabela>

      <Gaveta
        aberta={g.aberta}
        largura="lg"
        titulo={g.item ? 'Editar produto' : 'Novo produto'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-produto"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario id="form-produto" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo label="Código" erro={g.erros.codigo}>
              {(p) => (
                <Input
                  {...p}
                  name="codigo"
                  defaultValue={g.item?.codigo}
                  className="font-mono uppercase"
                  autoFocus
                />
              )}
            </Campo>
            <Campo label="Linha" erro={g.erros.linhaId}>
              {(p) => (
                <Select {...p} name="linhaId" defaultValue={g.item?.linhaId ?? ''}>
                  <option value="">Sem linha</option>
                  {linhas.data?.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.nome}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>
          </div>
          <Campo label="Nome" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} />}
          </Campo>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              label="Tamanho da ficha (peças)"
              erro={g.erros.tamanhoLote}
              dica="Pedido é dividido em fichas deste tamanho."
            >
              {(p) => (
                <Input
                  {...p}
                  name="tamanhoLote"
                  type="number"
                  min={1}
                  defaultValue={g.item?.tamanhoLote ?? 80}
                />
              )}
            </Campo>
            <Campo
              label="Múltiplo da caixa"
              erro={g.erros.multiploCaixa}
              dica="Opcional. Ex.: 80 volantes por caixa."
            >
              {(p) => (
                <Input
                  {...p}
                  name="multiploCaixa"
                  type="number"
                  min={1}
                  defaultValue={g.item?.multiploCaixa ?? ''}
                />
              )}
            </Campo>
          </div>
          <Checkbox name="ativo" label="Ativo" defaultChecked={g.item?.ativo ?? true} />

          <fieldset className="mt-2 flex flex-col gap-3">
            <legend className="mb-1 font-semibold">Sequência de produção</legend>
            {temHistorico && (
              <p className="text-xs text-amarelo">
                Este produto já tem fichas produzidas: dá para ajustar nome, setor e tempos, mas não
                adicionar, remover ou reordenar etapas.
              </p>
            )}
            {g.erros.etapas && (
              <p role="alert" className="text-xs font-medium text-vermelho">
                {g.erros.etapas}
              </p>
            )}
            {etapas.map((e, i) => (
              <div
                key={i}
                className="grid grid-cols-[2rem_1fr_1fr] items-start gap-x-2 gap-y-3 rounded-xl bg-elevated p-3"
              >
                <span className="pt-8 text-center text-sm font-semibold text-fg-subtle tabular">
                  {i + 1}
                </span>
                <Campo label="Setor" erro={g.erros[`etapas.${i}.setorId`]}>
                  {(p) => (
                    <Select
                      {...p}
                      value={e.setorId}
                      onChange={(ev) =>
                        mudarEtapa(i, {
                          setorId: ev.target.value,
                          nome:
                            e.nome ||
                            (setores.data?.find((s) => s.id === ev.target.value)?.nome ?? ''),
                        })
                      }
                    >
                      <option value="">Escolha…</option>
                      {setores.data?.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nome}
                        </option>
                      ))}
                    </Select>
                  )}
                </Campo>
                <Campo label="Nome da etapa" erro={g.erros[`etapas.${i}.nome`]}>
                  {(p) => (
                    <Input
                      {...p}
                      value={e.nome}
                      onChange={(ev) => mudarEtapa(i, { nome: ev.target.value })}
                    />
                  )}
                </Campo>
                <span aria-hidden />
                <Campo label="Peças por hora" erro={g.erros[`etapas.${i}.pecasPorHora`]}>
                  {(p) => (
                    <Input
                      {...p}
                      type="number"
                      min={1}
                      step="any"
                      value={e.pecasPorHora || ''}
                      onChange={(ev) => mudarEtapa(i, { pecasPorHora: Number(ev.target.value) })}
                    />
                  )}
                </Campo>
                <div className="flex items-start gap-2">
                  <Campo
                    label="Pré-set (s)"
                    erro={g.erros[`etapas.${i}.tempoPresetSeg`]}
                    className="min-w-0 flex-1"
                  >
                    {(p) => (
                      <Input
                        {...p}
                        type="number"
                        min={0}
                        value={e.tempoPresetSeg}
                        onChange={(ev) =>
                          mudarEtapa(i, { tempoPresetSeg: Number(ev.target.value) })
                        }
                      />
                    )}
                  </Campo>
                  <div className="flex shrink-0 gap-1 pt-7">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Subir etapa"
                      disabled={temHistorico || i === 0}
                      onClick={() => mover(i, -1)}
                    >
                      <ArrowUp className="size-4" aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Descer etapa"
                      disabled={temHistorico || i === etapas.length - 1}
                      onClick={() => mover(i, 1)}
                    >
                      <ArrowDown className="size-4" aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Remover etapa"
                      disabled={temHistorico || etapas.length === 1}
                      onClick={() => setEtapas((es) => es.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {!temHistorico && (
              <Button
                variant="secondary"
                size="sm"
                className="self-start"
                onClick={() => setEtapas((es) => [...es, etapaVazia()])}
              >
                <Plus className="size-4" aria-hidden /> Adicionar etapa
              </Button>
            )}
          </fieldset>
        </Formulario>
      </Gaveta>
    </>
  )
}
